import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

// ===== Shared animated styling for both pages =====
const sharedPageStyle = `
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes floatBlob {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(30px, -30px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.95); }
  }
  @keyframes floatBlob2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(-40px, 30px) scale(1.15); }
    66% { transform: translate(25px, -25px) scale(0.9); }
  }
  @keyframes gridMove {
    0% { background-position: 0 0; }
    100% { background-position: 60px 60px; }
  }
  @keyframes ringPulse {
    0% { transform: scale(0.9); opacity: 0.7; }
    70% { transform: scale(1.6); opacity: 0; }
    100% { transform: scale(1.6); opacity: 0; }
  }
  @keyframes shimmerSweep {
    0% { transform: translateX(-120%) skewX(-20deg); }
    100% { transform: translateX(220%) skewX(-20deg); }
  }
  @keyframes searchGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(67, 56, 202, 0.15), 0 10px 30px -10px rgba(67, 56, 202, 0.2); }
    50% { box-shadow: 0 0 0 4px rgba(67, 56, 202, 0.08), 0 14px 40px -10px rgba(67, 56, 202, 0.35); }
  }
  @keyframes searchBorderShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes sectionBorderGlow {
    0%, 100% { border-color: rgba(199, 210, 254, 0.6); box-shadow: 0 0 40px -10px rgba(99, 102, 241, 0.1); }
    50% { border-color: rgba(167, 139, 250, 0.8); box-shadow: 0 0 60px -10px rgba(99, 102, 241, 0.25); }
  }

  /* Hero-like wrapper with animated background */
  .listing-hero-wrap {
    position: relative;
    border-radius: 32px;
    padding: 3rem 2.5rem;
    margin: 1rem auto 2rem;
    max-width: 1240px;
    background:
      linear-gradient(135deg, rgba(238, 242, 255, 0.9) 0%, rgba(245, 243, 255, 0.95) 40%, rgba(237, 233, 254, 0.9) 100%);
    border: 1px solid rgba(199, 210, 254, 0.6);
    overflow: hidden;
    isolation: isolate;
    animation: sectionBorderGlow 6s ease-in-out infinite;
  }
  .listing-hero-wrap::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(99, 102, 241, 0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(99, 102, 241, 0.06) 1px, transparent 1px);
    background-size: 60px 60px;
    animation: gridMove 20s linear infinite;
    z-index: -2;
    -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 80%);
    mask-image: radial-gradient(ellipse at center, black 30%, transparent 80%);
  }
  .listing-hero-wrap .blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    z-index: -1;
    pointer-events: none;
  }
  .listing-hero-wrap .blob-1 {
    width: 320px; height: 320px;
    top: -120px; left: -80px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.35), transparent 70%);
    animation: floatBlob 14s ease-in-out infinite;
  }
  .listing-hero-wrap .blob-2 {
    width: 380px; height: 380px;
    bottom: -150px; right: -100px;
    background: radial-gradient(circle, rgba(167, 139, 250, 0.3), transparent 70%);
    animation: floatBlob2 18s ease-in-out infinite;
  }
  .listing-hero-wrap::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, transparent, #4338CA, #818CF8, #A78BFA, #818CF8, #4338CA, transparent);
    background-size: 200% 100%;
    animation: gradientShift 5s ease infinite;
    z-index: 2;
  }
  .listing-deco-ring {
    position: absolute;
    border-radius: 50%;
    border: 2px solid rgba(99, 102, 241, 0.25);
    pointer-events: none;
    z-index: -1;
  }
  .listing-deco-ring-1 { width: 80px; height: 80px; top: 40px; right: 60px; animation: ringPulse 4s ease-out infinite; }
  .listing-deco-ring-2 { width: 120px; height: 120px; bottom: 50px; left: 40px; animation: ringPulse 5s ease-out infinite; animation-delay: 1s; border-color: rgba(167, 139, 250, 0.3); }

  .listing-eyebrow {
    font-size: 0.72rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    font-weight: 800;
    background: linear-gradient(90deg, #4338CA, #7C3AED);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
  }
  .listing-title {
    font-weight: 800;
    letter-spacing: -0.02em;
    margin-top: 0.25rem;
  }

  /* Beautiful listing search box */
  .listing-search-wrap {
    position: relative;
    margin-top: 1.25rem;
    max-width: 520px;
    border-radius: 14px;
    padding: 2px;
    background: linear-gradient(120deg, #C7D2FE, #A78BFA, #818CF8, #C7D2FE);
    background-size: 300% 300%;
    animation: searchBorderShift 6s ease infinite, searchGlow 3s ease-in-out infinite;
    transition: transform 0.25s ease;
  }
  .listing-search-wrap:focus-within {
    transform: translateY(-1px);
    animation: searchBorderShift 3s ease infinite, searchGlow 1.8s ease-in-out infinite;
  }
  .listing-search-inner {
    position: relative;
    border-radius: 12px;
    background: #ffffff;
    overflow: hidden;
    display: flex;
    align-items: center;
  }
  .listing-search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #818CF8;
    pointer-events: none;
    transition: color 0.25s ease;
  }
  .listing-search-wrap:focus-within .listing-search-icon { color: #4338CA; }
  .listing-search-field {
    width: 100%;
    border: none !important;
    outline: none !important;
    background: transparent !important;
    padding: 0.85rem 1rem 0.85rem 2.8rem !important;
    font-size: 1rem;
    color: #1a202c;
    box-shadow: none !important;
  }
  .listing-search-field::placeholder { color: #a0aec0; font-weight: 400; }
  .listing-search-inner::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(238, 242, 255, 0.6), transparent);
    transform: translateX(-120%) skewX(-20deg);
    pointer-events: none;
  }
  .listing-search-wrap:focus-within .listing-search-inner::before {
    animation: shimmerSweep 1.8s ease-in-out infinite;
  }
  .listing-search-clear {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: #EEF2FF;
    color: #6366F1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .listing-search-clear:hover {
    background: #4338CA;
    color: #fff;
    transform: translateY(-50%) scale(1.08);
  }

  /* Generic listing card (used for cities & categories) */
  .listing-card {
    position: relative;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 18px;
    padding: 1.15rem 1.25rem;
    height: 100%;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 0.9rem;
    box-shadow: 0 2px 8px rgba(67, 56, 202, 0.04);
    text-decoration: none;
    color: inherit;
  }
  .listing-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, #4338CA, #6366F1, #818CF8, #A78BFA);
    background-size: 300% 300%;
    animation: gradientShift 4s ease infinite;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .listing-card::after {
    content: '';
    position: absolute;
    right: -40px;
    bottom: -40px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(99,102,241,0.08), transparent 70%);
    transition: all 0.4s ease;
    pointer-events: none;
  }
  .listing-card:hover {
    transform: translateY(-5px);
    border-color: #C7D2FE;
    background: #ffffff;
    box-shadow: 0 18px 40px -18px rgba(67, 56, 202, 0.35);
  }
  .listing-card:hover::before { opacity: 1; }
  .listing-card:hover::after {
    transform: scale(1.6);
    background: radial-gradient(circle, rgba(99,102,241,0.14), transparent 70%);
  }
  .listing-icon-wrap {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    font-weight: 800;
    color: #4338CA;
    background: linear-gradient(135deg, #EEF2FF, #E0E7FF);
    flex-shrink: 0;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    z-index: 1;
  }
  .listing-card:hover .listing-icon-wrap {
    background: linear-gradient(135deg, #4338CA, #6366F1);
    color: #fff;
    transform: rotate(-6deg) scale(1.06);
    box-shadow: 0 8px 20px -6px rgba(67, 56, 202, 0.5);
  }
  .listing-card-body {
    flex: 1;
    min-width: 0;
    position: relative;
    z-index: 1;
  }
  .listing-card-name {
    font-weight: 700;
    color: #1a202c;
    font-size: 1rem;
    line-height: 1.25;
  }
  .listing-card-count {
    font-size: 0.8rem;
    color: #94a3b8;
    margin-top: 2px;
    transition: color 0.3s ease;
  }
  .listing-card:hover .listing-card-count { color: #6366F1; }
  .listing-card-arrow {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #F5F3FF;
    opacity: 0;
    transform: translateX(-6px);
    transition: all 0.35s ease;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
  }
  .listing-card:hover .listing-card-arrow {
    opacity: 1;
    transform: translateX(0);
  }

  .listing-empty {
    text-align: center;
    padding: 3rem 1rem;
    color: #a0aec0;
  }
  .listing-empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
  }

  @media (max-width: 768px) {
    .listing-hero-wrap { padding: 2rem 1.25rem; border-radius: 24px; }
    .listing-deco-ring { display: none; }
  }
`;

// ===== Categories (redesigned with search) =====
export function Categories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/categories');
        setCats(r.data.categories || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Live filter by name
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return cats;
    return cats.filter(c => c.name.toLowerCase().includes(term));
  }, [cats, query]);

  return (
    <div className="container my-4" data-testid="categories-page">
      <style>{sharedPageStyle}</style>

      <div className="listing-hero-wrap">
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="listing-deco-ring listing-deco-ring-1" />
        <div className="listing-deco-ring listing-deco-ring-2" />

        <div className="position-relative" style={{ zIndex: 1 }}>
          <div className="listing-eyebrow">Browse by type</div>
          <h1 className="listing-title">All categories</h1>
          <p className="text-muted-2 small mb-0 mt-1">
            Explore top-rated businesses across every category — search to find yours.
          </p>

          {/* Search box */}
          <div className="listing-search-wrap">
            <div className="listing-search-inner">
              <span className="listing-search-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </span>
              <input
                className="form-control listing-search-field"
                placeholder="Search a category, e.g. Restaurants, Gyms…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="categories-search-input"
                aria-label="Search categories"
              />
              {query && (
                <button
                  type="button"
                  className="listing-search-clear"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 2L8 8M8 2L2 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Small count line */}
          {!loading && (
            <div className="small text-muted-2 mt-2" data-testid="categories-count">
              {filtered.length} {filtered.length === 1 ? 'category' : 'categories'}
              {query.trim() ? ` matching "${query.trim()}"` : ' available'}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5"><span className="ap-loader" /></div>
      ) : filtered.length === 0 ? (
        <div className="listing-empty" data-testid="categories-empty">
          <div className="listing-empty-icon">🗂️</div>
          <div className="fw-semibold" style={{ color: '#4a5568' }}>
            No categories found{query.trim() ? ` for "${query.trim()}"` : ''}
          </div>
          <div className="small mt-1">Try a different search term.</div>
        </div>
      ) : (
        <div className="row g-3">
          {filtered.map(c => (
            <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={c.id}>
              <Link
                to={`/search?category=${c.slug}`}
                className="listing-card"
                data-testid={`cat-${c.slug}`}
              >
                <div className="listing-icon-wrap">
                  {c.name.substring(0, 1).toUpperCase()}
                </div>
                <div className="listing-card-body">
                  <div className="listing-card-name">{c.name}</div>
                  <div className="listing-card-count">
                    {c.business_count != null
                      ? `${c.business_count} ${c.business_count === 1 ? 'business' : 'businesses'}`
                      : 'Explore businesses'}
                  </div>
                </div>
                <div className="listing-card-arrow">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 9L9 3M9 3H4M9 3V8" stroke="#4338CA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Cities (redesigned with search) =====
export function Cities() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/cities');
        setCities(r.data.cities || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Live filter by name
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return cities;
    return cities.filter(c => c.name.toLowerCase().includes(term));
  }, [cities, query]);

  return (
    <div className="container my-4" data-testid="cities-page">
      <style>{sharedPageStyle}</style>

      <div className="listing-hero-wrap">
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="listing-deco-ring listing-deco-ring-1" />
        <div className="listing-deco-ring listing-deco-ring-2" />

        <div className="position-relative" style={{ zIndex: 1 }}>
          <div className="listing-eyebrow">By location</div>
          <h1 className="listing-title">Explore cities</h1>
          <p className="text-muted-2 small mb-0 mt-1">
            Discover trusted local businesses in every city — search to find yours.
          </p>

          {/* Search box */}
          <div className="listing-search-wrap">
            <div className="listing-search-inner">
              <span className="listing-search-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </span>
              <input
                className="form-control listing-search-field"
                placeholder="Search a city, e.g. Mumbai, Pune…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="cities-search-input"
                aria-label="Search cities"
              />
              {query && (
                <button
                  type="button"
                  className="listing-search-clear"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 2L8 8M8 2L2 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Small count line */}
          {!loading && (
            <div className="small text-muted-2 mt-2" data-testid="cities-count">
              {filtered.length} {filtered.length === 1 ? 'city' : 'cities'}
              {query.trim() ? ` matching "${query.trim()}"` : ' available'}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5"><span className="ap-loader" /></div>
      ) : filtered.length === 0 ? (
        <div className="listing-empty" data-testid="cities-empty">
          <div className="listing-empty-icon">🏙️</div>
          <div className="fw-semibold" style={{ color: '#4a5568' }}>
            No cities found{query.trim() ? ` for "${query.trim()}"` : ''}
          </div>
          <div className="small mt-1">Try a different search term.</div>
        </div>
      ) : (
        <div className="row g-3">
          {filtered.map(ci => (
            <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={ci.id}>
              <Link
                to={`/search?city=${ci.slug}`}
                className="listing-card"
                data-testid={`city-${ci.slug}`}
              >
                <div className="listing-icon-wrap">
                  {ci.name.substring(0, 1).toUpperCase()}
                </div>
                <div className="listing-card-body">
                  <div className="listing-card-name">{ci.name}</div>
                  <div className="listing-card-count">
                    {ci.business_count != null
                      ? `${ci.business_count} ${ci.business_count === 1 ? 'business' : 'businesses'}`
                      : 'Explore businesses'}
                  </div>
                </div>
                <div className="listing-card-arrow">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 9L9 3M9 3H4M9 3V8" stroke="#4338CA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}